import isNil from 'lodash/isNil';
import curry from 'lodash/curry';
import find from 'lodash/find';
import flow from 'lodash/flow';
import first from 'lodash/first';
import lodashIsEqual from 'lodash/isEqual';
import lodashReduce from 'lodash/reduce';
import { DEFAULT_BROADCAST_NAME_V1 } from '@app/dashboard/messaging/channel';
import { fullName, nameInitials } from '@lib/utils';
import { getSessionData } from '@lib/session-data';
import { moment } from '@app/lib/moment';
// Handles channel list message previews before file improvements enabled
export function getMessageBody(message) {
    var _a, _b;
    if (isNil(message))
        return '';
    let rawMessage;
    if (((_a = message === null || message === void 0 ? void 0 : message.customType) === null || _a === void 0 ? void 0 : _a.indexOf('image/')) > -1 || // This is the original condition
        (message.messageType === 'file' && (message === null || message === void 0 ? void 0 : message.customType) === '')) {
        // adding check above this line to handle (ES-1349), custom_type field is empty for old layer images messages
        rawMessage = $localize `Image`;
    }
    else if (((_b = message === null || message === void 0 ? void 0 : message.customType) === null || _b === void 0 ? void 0 : _b.indexOf('location')) > -1) {
        rawMessage = $localize `Location`;
    }
    else if ((message === null || message === void 0 ? void 0 : message.messageType) === 'admin') {
        rawMessage = $localize `Participants Modified`;
    }
    else {
        const userMessage = message;
        rawMessage = userMessage && (userMessage === null || userMessage === void 0 ? void 0 : userMessage.message);
    }
    return rawMessage;
}
export function loggedInUser() {
    const user = getLoggedInUser();
    return {
        lastSeenAt: null,
        metaData: { first_name: user.firstName, last_name: user.lastName },
        nickname: user.displayName,
        userId: user.id,
    };
}
export function getLoggedInUser() {
    const user = getSessionData();
    return {
        id: user.id.toString(),
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        displayName: fullName(user),
        role: user.role,
        initials: nameInitials(user),
    };
}
export function findConversationByParticipants(channels = [], participantsIds) {
    participantsIds = participantsIds.sort();
    return find(channels, (channel) => lodashIsEqual(participantsIds, channel && channel.members.map(p => p.userId).sort()));
}
export function findConversationsByParticipants(channels = [], participantsIds) {
    participantsIds = participantsIds.sort();
    return channels.filter((channel) => lodashIsEqual(participantsIds, channel && channel.members.map(p => p.userId).sort()));
}
export function findChannelByParticipants(channels = [], participantsIds) {
    participantsIds = participantsIds.sort();
    return find(channels, (channel) => lodashIsEqual(participantsIds, channel && channel.members.map(p => p.userId).sort()));
}
export function getLoggedInUserId() {
    var _a;
    return (_a = getSessionData()) === null || _a === void 0 ? void 0 : _a.id.toString();
}
export const binaryInsert = curry((sort, property, predicate, arr, val) => {
    let stopIndex = arr.length;
    if (stopIndex === 0) {
        arr.push(val);
        return;
    }
    let startIndex = 0;
    let middle = Math.floor((stopIndex + startIndex) / 2);
    let valLessThanMiddle;
    let valGreaterThanMiddle;
    while (!predicate(val, middle, arr) && startIndex < stopIndex) {
        valLessThanMiddle = property(val) < property(arr[middle]);
        valGreaterThanMiddle = property(val) > property(arr[middle]);
        if (sort === 'asc' ? valLessThanMiddle : valGreaterThanMiddle) {
            stopIndex = middle - 1;
        }
        else if (sort === 'asc' ? valGreaterThanMiddle : valLessThanMiddle) {
            startIndex = middle + 1;
        }
        middle = Math.floor((stopIndex + startIndex) / 2);
    }
    arr.splice(middle, 0, val);
});
// From Legacy Messaging V1
export function mapToMoment(timeStamp) {
    return moment(timeStamp);
}
export const filter = curry((iteratee, collection) => collection.filter(iteratee));
export const genUniqId = () => getLoggedInUserId() + '.' + new Date().getTime();
const _excludeLoggedInUser = participant => participant.userId !== getLoggedInUserId();
export const getOtherParticipantObject = flow([filter(_excludeLoggedInUser), first]);
export const getOtherParticipants = filter(_excludeLoggedInUser);
// transform array of users to object of users with keys as user ids
export function usersArrayToObject(users) {
    const mapUser = (_users, user) => {
        user = user.user;
        user.name = fullName(user, true);
        user.initials = nameInitials(user);
        user.id = user.id.toString();
        _users[user.id] = user;
        return _users;
    };
    return lodashReduce(users.users, mapUser, {});
}
// Prevents issue sending message to broadcasts from V1
// V1 broadcasts don't have names
export function getChannelName(selectedChannel) {
    const selectedChannelName = selectedChannel === null || selectedChannel === void 0 ? void 0 : selectedChannel.sdkInstance.name;
    if (!selectedChannelName || selectedChannelName === DEFAULT_BROADCAST_NAME_V1) {
        return null;
    }
    return selectedChannelName;
}
